/**
 * `placeAnchored` — the one function that decides where an overlay goes (parity cards **P-007**,
 * **P-008**, round 7a).
 *
 * Round 6h's sweep found three families of the same defect, on three different primitives that
 * each did their own arithmetic:
 *
 *   - the add-column gallery opened at **(8, 8)** — the top-left corner of the window — because
 *     the trigger's rectangle resolved to nothing and the clamp's lower bound *is* the margin, so
 *     "no anchor" silently became "the opposite corner of the screen" (P-007);
 *   - the board toolbar's panes are authored at their measured desktop widths (556–800 px) and
 *     nothing narrowed them on a 390 px phone, so `right` read 689–1,089 (P-008);
 *   - `menu#notifications-pane-menu` was positioned by CSS against its pane instead of its
 *     button, and a phone width rule pushed it to `left: −44` (P-008).
 *
 * So the arithmetic lives here, once, as a pure function over rectangles — no DOM, no React —
 * and `placement.test.ts` proves each rule. Its callers pass their own gap and flip policy so
 * that **desktop geometry is byte-identical** to what the captures measured: `AnchoredPopover`
 * sits 4 px under its button and flips; the board's `Menu` opens *at* the click point (gap 0)
 * and pins to the viewport rather than flipping, which is what it has always done.
 *
 * The grid's `CellPopover` is deliberately **not** a caller: it pins a too-tall editor to the
 * top margin where this function pins to the bottom, and `verify-editors-local` measures all 27
 * of those editors. It is a sheet on a phone (F9-D6) and was not in any of the sweep's overlay
 * failures, so converting it would be geometry risk for no defect.
 *
 * The rules, in order:
 *   1. an overlay never exceeds the viewport: `maxWidth` / `maxHeight` are always returned, so a
 *      440 px menu on a 390 px phone is narrowed rather than cropped;
 *   2. it prefers below the anchor, flips above when it does not fit and there is more room
 *      there, and otherwise pins to the viewport and scrolls inside itself;
 *   3. it clamps into the viewport with an 8 px margin on both axes;
 *   4. **a degenerate anchor is not an anchor**: `isUsableAnchor` rejects `(0, 0, 0, 0)` — what an
 *      element that is `display: none`, detached or not yet laid out reports — so the caller can
 *      keep its last good position instead of painting a menu in the corner of the window.
 */
/** The viewport margin every overlay keeps, shared by all of them. */
export const OVERLAY_MARGIN = 8;
/** The gap a popover opened from a *button* leaves under it; a click-point menu passes 0. */
export const OVERLAY_GAP = 4;
/**
 * A rectangle that can be trusted as an anchor. A zero-size box at the origin is what
 * `getBoundingClientRect()` returns for a detached or `display: none` element, and treating it
 * as a real anchor is exactly how the gallery ended up at (8, 8). A **point** anchor (zero size,
 * non-zero coordinates) is legitimate: that is a context menu at the cursor.
 */
export function isUsableAnchor(rect) {
    if (!rect)
        return false;
    if (!Number.isFinite(rect.x) || !Number.isFinite(rect.y))
        return false;
    if (rect.width < 0 || rect.height < 0)
        return false;
    return rect.width > 0 || rect.height > 0 || rect.x !== 0 || rect.y !== 0;
}
export function placeAnchored(anchor, size, viewport, options = {}) {
    const { align = 'start', gap = OVERLAY_GAP, margin = OVERLAY_MARGIN, flip = true } = options;
    const maxWidth = Math.max(0, viewport.width - margin * 2);
    const maxHeight = Math.max(0, viewport.height - margin * 2);
    const width = Math.min(size.width, maxWidth);
    const height = Math.min(size.height, maxHeight);
    const anchorBottom = anchor.y + anchor.height;
    // Rule 2. `height === 0` is the first pass, before the overlay has been measured: it opens
    // below, and the layout effect re-places it once it knows how tall it is.
    let below = true;
    let top = anchorBottom + gap;
    if (height > 0 && top + height > viewport.height - margin) {
        const above = anchor.y - gap - height;
        if (flip && above >= margin) {
            below = false;
            top = above;
        }
    }
    top = Math.max(margin, Math.min(top, Math.max(margin, viewport.height - height - margin)));
    const preferred = align === 'end' ? anchor.x + anchor.width - width : anchor.x;
    const left = Math.max(margin, Math.min(preferred, Math.max(margin, viewport.width - width - margin)));
    return { top, left, maxWidth, maxHeight, below };
}
//# sourceMappingURL=placement.js.map